import * as React from 'react';
import { TButtonTheme } from '@cian/ui-kit/button/types';
import { useToast } from '@cian/ui-kit/toast';
import { useDispatch, useSelector } from 'react-redux';

import { requestAuthentication } from '../../actions/authentication';
import { setComparisonStatus } from '../../actions/newbuilding';
import { getComparisonStatus, getGaLabel, getIsAuthenticated, getNewbuildingId } from '../../selectors/newbuilding';
import {
  addNewbuildingToComparison,
  type IComparisonActionResult,
  removeNewbuildingFromComparison,
} from '../../services/propertyComparison';
import { useApplicationContext } from '../../utils/withApplicationContext';
import { CompareButtonControl } from './CompareButtonControl';
import { CompareButtonOnboarding } from './CompareButtonOnboarding';
import { CompareToastContent } from './CompareToastContent';
import { COMPARE_ENABLED_CONFIG } from './constants';
import {
  trackAddNewbuildingToComparison,
  trackClickAddComparisonPopup,
  trackClickRemoveComparisonPopup,
  trackRemoveNewbuildingFromComparison,
  type TComparisonEventSource,
} from './tracking';
import { useCompareOnboarding } from './useCompareOnboarding';
import { TThunkDispatch } from '../../types/redux/thunk';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте еще раз';

interface INewbuildingCompareButtonProps {
  hideText?: boolean;
  isHeader?: boolean;
  buttonTheme?: TButtonTheme;
  addToCompareText?: string;
  withOnboarding?: boolean;
  source: TComparisonEventSource;
}

export const NewbuildingCompareButton: React.FC<INewbuildingCompareButtonProps> = ({
  hideText = false,
  isHeader = false,
  buttonTheme,
  addToCompareText,
  withOnboarding = false,
  source,
}) => {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch: TThunkDispatch = useDispatch();
  const comparisonStatus = useSelector(getComparisonStatus);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const newbuildingId = useSelector(getNewbuildingId);
  const gaLabel = useSelector(getGaLabel);
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isCompareEnabled = config.get<boolean>(COMPARE_ENABLED_CONFIG) || false;
  const isAdded = comparisonStatus === 'added';
  const isComparisonAvailable = comparisonStatus === 'added' || comparisonStatus === 'available';
  const { open, closeHandler } = useCompareOnboarding(withOnboarding && isCompareEnabled && isComparisonAvailable);

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult, comparisonAction?: 'add' | 'remove') => {
      const onComparisonClick =
        comparisonAction === 'add' ? trackClickAddComparisonPopup : trackClickRemoveComparisonPopup;

      toast.open({
        type: result.success === false ? 'error' : 'success',
        hasCloseIcon: false,
        content: (
          <CompareToastContent
            text={result.text}
            comparisonUrl={result.comparisonUrl}
            onClick={() => onComparisonClick(newbuildingId)}
          />
        ),
      });
    },
    [toast, newbuildingId],
  );

  const handleClick = React.useCallback(async () => {
    const previousStatus = comparisonStatus;
    const nextStatus = isAdded ? 'available' : 'added';

    if (!isAuthenticated) {
      dispatch(requestAuthentication({}));

      return;
    }

    dispatch(setComparisonStatus(nextStatus));

    try {
      const result = !isAdded
        ? await addNewbuildingToComparison({ httpApi, logger, newbuildingId })
        : await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId });

      if (result.success === false) {
        showComparisonToast(result);
        dispatch(setComparisonStatus(previousStatus));

        return;
      }

      showComparisonToast(result, isAdded ? 'remove' : 'add');

      if (!isAdded) {
        trackAddNewbuildingToComparison(newbuildingId, gaLabel, source);
      } else {
        trackRemoveNewbuildingFromComparison(newbuildingId, gaLabel, source);
      }
    } catch (_error) {
      dispatch(setComparisonStatus(previousStatus));
      showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT });
    }
  }, [
    comparisonStatus,
    dispatch,
    gaLabel,
    httpApi,
    isAdded,
    isAuthenticated,
    logger,
    newbuildingId,
    showComparisonToast,
    source,
  ]);

  if (!isCompareEnabled || !isComparisonAvailable) {
    return null;
  }

  const button = (
    <CompareButtonControl
      hideText={hideText}
      isHeader={isHeader}
      buttonTheme={buttonTheme}
      addToCompareText={addToCompareText}
      isAdded={isAdded}
      onClick={handleClick}
    />
  );

  if (!withOnboarding || !open) {
    return button;
  }

  return (
    <CompareButtonOnboarding open={open} onClose={closeHandler}>
      {button}
    </CompareButtonOnboarding>
  );
};
